/**
 * Operator-facing presentation helpers.
 * Pure display transforms — do not change stored VerificationResult / evidence strings.
 */

/** Rewrite contradictory or developer-oriented health messages for Level 1–2 UI. */
export function operatorFacingMessage(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const text = raw.trim();
  if (!text) return null;

  // Legacy stored messages may still say unexpected status "ok" from older probes.
  const unexpectedOk = text.match(
    /Unexpected application health status:\s*['"]?ok['"]?/i,
  );
  if (unexpectedOk) {
    return "Application Healthy";
  }

  const unexpectedQuoted = text.match(
    /Unexpected application health status:\s*['"]([^'"]+)['"]/i,
  );
  if (unexpectedQuoted) {
    const status = unexpectedQuoted[1].toLowerCase();
    if (status === "healthy" || status === "ok" || status === "up") {
      return "Application Healthy";
    }
    if (status === "degraded" || status === "warn" || status === "warning") {
      return "Application Degraded";
    }
    if (status === "unhealthy" || status === "down" || status === "fail") {
      return "Application Critical";
    }
  }

  if (/^AI Secure application responded \(status=ok\)/i.test(text)) {
    return "Application Healthy";
  }
  if (/^AI Secure application responded \(status=healthy\)/i.test(text)) {
    return "Application Healthy";
  }
  if (/^AI Secure application responded \(status=degraded\)/i.test(text)) {
    return "Application Degraded";
  }
  if (/^AI Secure application responded \(status=unhealthy\)/i.test(text)) {
    return "Application Critical";
  }

  return text;
}

/**
 * Short operator recommendation for Level 1–2.
 * Full text remains available in diagnostics / expanded details.
 */
export function shortOperatorRecommendation(
  raw: string | null | undefined,
): string | null {
  if (raw == null) return null;
  let text = raw.trim();
  if (!text) return null;

  text = text.replace(/^Recommendation:\s*/i, "");

  if (/verify tenant API is running and reachable/i.test(text)) {
    return "Restart tenant API";
  }
  if (/point .+ A\/AAAA to origin/i.test(text)) {
    return "Fix DNS records to origin IP";
  }
  if (/create DNS for .+ →/i.test(text)) {
    return "Create DNS A/AAAA record";
  }
  if (/fix TLS for .+ at Cloudflare/i.test(text)) {
    return "Fix TLS at Cloudflare";
  }
  if (/renew or reissue TLS/i.test(text)) {
    return "Renew TLS certificate";
  }
  if (/renew TLS for .+ before expiry/i.test(text)) {
    return "Renew TLS before expiry";
  }
  if (/Open Runtime Doctor report and resolve failing checks/i.test(text)) {
    return "Resolve failing Runtime Doctor checks";
  }
  if (/Disable CP_AUTH_DISABLED in production/i.test(text)) {
    return "Enable authentication in production";
  }
  if (/Configure .+ and run a successful connection test/i.test(text)) {
    return "Configure and test connection";
  }
  if (/Run a live connection test for/i.test(text)) {
    return "Run connection test";
  }
  if (/Refresh operations to run worker continuous verification/i.test(text)) {
    return "Refresh operations";
  }

  // Prefer first sentence / clause for long developer copy.
  const firstSentence = text.split(/(?<=[.!?])\s+/)[0] ?? text;
  if (firstSentence.length > 72) {
    return `${firstSentence.slice(0, 69).trimEnd()}…`;
  }
  return firstSentence;
}

/** Relative age for compact cards when ISO timestamps are available. */
export function compactRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const deltaSec = Math.round((Date.now() - ms) / 1000);
  if (deltaSec < 45) return "just now";
  if (deltaSec < 90) return "1 min ago";
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)} min ago`;
  if (deltaSec < 86400) {
    const hours = Math.round(deltaSec / 3600);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }
  const days = Math.round(deltaSec / 86400);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}
